import { fetchSession, unavailableMessage, type FieldErrors } from "../../api/client.js";

import type { Estimate } from "./estimate-sheet.js";
import { estimateApi } from "./urls.js";

/**
 * 見積書の作成（<c>POST /api/estimate</c>）。<b>行き先ではなく保存した見積書を返す</b>ので、
 * 共有の <c>postForm</c>（<c>{ location }</c> を受ける）とは別に持つ。トークンの扱いは同じ（送る直前に取り直す）。
 * ログインは要らない（401・404 は返らない作り。返ってきたら読めなかったときの断りにする）。
 * 作れたときは、トークンと一緒に受け取った<b>ログインしているか</b>も返す（「この内容で申し込む」の行き先を決める）。
 */
export type CreateOutcome =
    | { readonly kind: "created"; readonly value: Estimate; readonly signedIn: boolean }
    | { readonly kind: "errors"; readonly errors: FieldErrors };

interface Problem {
    readonly errors?: Record<string, string[]>;
}

export const postEstimate = async (body: URLSearchParams): Promise<CreateOutcome> => {
    const { token, signedIn } = await fetchSession();
    const response = await fetch(estimateApi.create, {
        method: "POST",
        body,
        headers: { Accept: "application/json", RequestVerificationToken: token },
    });

    // 叩きすぎ（docs/02 §14）。断りの文は本文にそのまま入っている
    if (response.status === 429) {
        return { kind: "errors", errors: { "": [await response.text()] } };
    }
    if (response.status === 400) {
        const problem = await response.json() as Problem;
        return { kind: "errors", errors: problem.errors ?? { "": [unavailableMessage] } };
    }
    if (!response.ok) {
        return { kind: "errors", errors: { "": [unavailableMessage] } };
    }
    return { kind: "created", value: await response.json() as Estimate, signedIn };
};
